import React, { useState, useEffect, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Button, message, Table, DatePicker, Select, Space, Divider } from 'antd';
import { FilterOutlined, FileExcelOutlined, ClearOutlined } from '@ant-design/icons';
import axios from 'axios';
import * as XLSX from 'xlsx';
import dayjs, { Dayjs } from 'dayjs';

interface BudgetHistoryRecord {
  Id: string;
  createdDate: string;
  usedBudget: number;
  usedFor: string;
  displayName: string;
  country: string;
}

const BudgetHistory: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [reportData, setReportData] = useState<BudgetHistoryRecord[]>([]);
  const [showReport, setShowReport] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [selectedUsedFor, setSelectedUsedFor] = useState<string | null>(null);

  // Check if dates are valid and end date is greater than start date
  const isValidDateRange = () => {
    if (!startDate || !endDate) return false;
    return endDate.isAfter(startDate);
  };

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  };

  const handleSubmit = async () => {
    const API_URL = process.env.REACT_APP_API_URL;

    if (!startDate || !endDate) {
      message.error('Please select both start and end dates');
      return;
    }

    if (!endDate.isAfter(startDate)) {
      message.error('End date must be greater than start date');
      return;
    }

    try {
      const token = await fetchToken();
      
      const response = await axios.get(`${API_URL}/api/admin/getBudgetHistory`, {
        params: {
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD')
        },
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      setReportData(response.data);
      setShowReport(true);
      setSelectedCountry(null);
      setSelectedUsedFor(null);
      console.log('Budget History Data:', response.data);
    } catch (error) {
      message.error(`Failed to get budget history: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleClear = () => {
    setReportData([]);
    setShowReport(false);
    setSelectedCountry(null);
    setSelectedUsedFor(null);
    setStartDate(null);
    setEndDate(null);
    setCurrentPage(1);
    message.success('Data cleared successfully');
  };

  const handleDownloadExcel = () => {
    if (reportData.length === 0) {
      message.error('No data available to download');
      return;
    }

    const formattedData = reportData.map(item => ({
      'User Name': item.displayName,
      'Country': item.country,
      'Used Budget': item.usedBudget,
      'Used For': item.usedFor,
      'Date': dayjs(item.createdDate).format('YYYY-MM-DD')
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'BudgetHistory');

    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];

    XLSX.writeFile(workbook, `BudgetHistory_${formattedDate}.xlsx`);
  };

  // Generate unique filters from data
  const countryFilters = useMemo(() => {
    const uniqueCountries = Array.from(new Set(reportData.map(item => item.country).filter(Boolean)));
    return uniqueCountries.sort().map(country => ({ label: country, value: country }));
  }, [reportData]);

  const usedForFilters = useMemo(() => {
    const uniqueUsedFor = Array.from(new Set(reportData.map(item => item.usedFor).filter(Boolean)));
    return uniqueUsedFor.sort().map(usedFor => ({ label: usedFor, value: usedFor }));
  }, [reportData]);

  // Filter data based on selected filters
  const filteredData = useMemo(() => {
    let data = [...reportData];
    
    if (selectedCountry) {
      data = data.filter(item => item.country === selectedCountry);
    }
    
    if (selectedUsedFor) {
      data = data.filter(item => item.usedFor === selectedUsedFor);
    }
    
    return data;
  }, [reportData, selectedCountry, selectedUsedFor]);

  const columns = [
    {
      title: 'User Name',
      dataIndex: 'displayName',
      key: 'displayName',
      sorter: (a: BudgetHistoryRecord, b: BudgetHistoryRecord) => a.displayName.localeCompare(b.displayName),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      sorter: (a: BudgetHistoryRecord, b: BudgetHistoryRecord) => (a.country || '').localeCompare(b.country || ''),
    },
    {
      title: 'Used Budget',
      dataIndex: 'usedBudget',
      key: 'usedBudget',
      sorter: (a: BudgetHistoryRecord, b: BudgetHistoryRecord) => a.usedBudget - b.usedBudget,
      render: (value: number) => value?.toFixed(2) || '0.00',
    },
    {
      title: 'Used For',
      dataIndex: 'usedFor',
      key: 'usedFor',
      ellipsis: true,
      sorter: (a: BudgetHistoryRecord, b: BudgetHistoryRecord) => (a.usedFor || '').localeCompare(b.usedFor || ''),
    },
    {
      title: 'Date',
      dataIndex: 'createdDate',
      key: 'createdDate',
      sorter: (a: BudgetHistoryRecord, b: BudgetHistoryRecord) => 
        dayjs(a.createdDate).unix() - dayjs(b.createdDate).unix(),
      render: (value: string) => dayjs(value).format('YYYY-MM-DD'),
    },
  ];

  return (
    <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
      <div style={{ 
        backgroundColor: 'white', 
        padding: '16px', 
        borderRadius: '8px', 
        marginBottom: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Space size="middle" wrap>
          <div>
            <div style={{ marginBottom: '6px', fontWeight: '500', color: '#595959', fontSize: '13px' }}>Start Date</div>
            <DatePicker
              value={startDate}
              onChange={(date) => setStartDate(date)}
              format="YYYY-MM-DD"
              style={{ width: '180px' }}
            />
          </div>
          <div>
            <div style={{ marginBottom: '6px', fontWeight: '500', color: '#595959', fontSize: '13px' }}>End Date</div>
            <DatePicker
              value={endDate}
              onChange={(date) => setEndDate(date)}
              format="YYYY-MM-DD"
              style={{ width: '180px' }}
              disabledDate={(current) => {
                // Disable dates that are before or equal to start date
                if (!startDate) return false;
                return current && current.isBefore(startDate, 'day');
              }}
            />
          </div>
          <div style={{ paddingTop: '22px' }}>
            <Space size="small">
              <Button 
                type="primary" 
                onClick={handleSubmit}
                disabled={!isValidDateRange()}
              >
                Get Budget History
              </Button>
              <Button 
                icon={<FileExcelOutlined />}
                onClick={handleDownloadExcel}
                style={reportData.length === 0 
                  ? {} 
                  : { backgroundColor: '#52c41a', borderColor: '#52c41a', color: 'white' }
                }
                disabled={reportData.length === 0}
              >
                Download Excel
              </Button>
              <Button 
                icon={<ClearOutlined />}
                onClick={handleClear}
                disabled={reportData.length === 0}
                danger
              >
                Clear
              </Button>
            </Space>
          </div>
        </Space>
      </div>
      {showReport && (
        <div>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '16px', 
            borderRadius: '8px', 
            marginBottom: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <Space size="middle" wrap>
              <div>
                <div style={{ marginBottom: '6px', fontWeight: '500', color: '#595959', fontSize: '13px' }}>Country</div>
                <Select
                  placeholder="Select Country"
                  style={{ width: 200 }}
                  allowClear
                  showSearch
                  options={countryFilters}
                  value={selectedCountry}
                  onChange={(value) => setSelectedCountry(value)}
                />
              </div>
              <div>
                <div style={{ marginBottom: '6px', fontWeight: '500', color: '#595959', fontSize: '13px' }}>Used For</div>
                <Select
                  placeholder="Select Award Type"
                  style={{ width: 200 }}
                  allowClear
                  showSearch
                  options={usedForFilters}
                  value={selectedUsedFor}
                  onChange={(value) => setSelectedUsedFor(value)}
                />
              </div>
            </Space>
          </div>
          <div style={{ 
            backgroundColor: 'white', 
            padding: '16px', 
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <Table
              dataSource={filteredData}
              columns={columns}
              rowKey="Id"
              pagination={{ 
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                },
                onShowSizeChange: (current, size) => {
                  setCurrentPage(1);
                  setPageSize(size);
                }
              }}
              scroll={{ x: 800 }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetHistory;
